import { ReactNode } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Form, useToaster } from '@crossnokaye/ux-common';

import { useCreateRefreshToken } from '@/features/tokens/hooks';

const createTokenSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});

type CreateTokenFormValues = z.infer<typeof createTokenSchema>;

interface CreateTokenFormProps {
  children: (form: UseFormReturn<CreateTokenFormValues>) => ReactNode;
  onSuccess: (token: string) => void;
}

export const CreateTokenForm = ({ children, onSuccess }: CreateTokenFormProps) => {
  const { initiateAuthRequest } = useCreateRefreshToken();
  const { addErrorToast } = useToaster();
  const form = useForm<CreateTokenFormValues>({
    resolver: zodResolver(createTokenSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const onSubmit = async (values: CreateTokenFormValues) => {
    try {
      const token = await initiateAuthRequest(values.name, values.description);
      if (token && typeof token === 'string') {
        onSuccess(token);
        form.reset();
      }
    } catch (error) {
      addErrorToast('Failed to create token');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={'space-y-6'}>
        {children(form)}
      </form>
    </Form>
  );
};
